import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { where } from 'firebase/firestore';
import useSongFeed from '../hooks/useSongFeed';
import useSongLikes from '../hooks/useSongLikes';
import useComments from '../hooks/useComments';
import useEmojiReactions from '../hooks/useEmojiReactions';
import useSearchSongs from '../hooks/useSearchSongs';
import logger from '../utils/logger';

const SongsContext = createContext();

export const useSongs = () => useContext(SongsContext);

export const SongsProvider = ({ children }) => {
  const feed = useSongFeed();
  const { likeSong, unlikeSong } = useSongLikes(feed.setSongs);
  const comments = useComments(feed.setSongs);
  const reactions = useEmojiReactions(feed.setSongs);
  const { searchSongs } = useSearchSongs();

  const triggerThumbnailUpdate = useCallback(async () => {
    try {
      const result = await httpsCallable(functions, 'triggerThumbnailUpdate')();
      logger.info('Thumbnail update completed:', result.data);
      return result.data;
    } catch (error) {
      logger.error('Error triggering thumbnail update:', error);
      throw error;
    }
  }, []);

  const value = useMemo(() => ({
    songs: feed.songs,
    totalSongCount: feed.totalSongCount,
    currentSong: feed.currentSong,
    setCurrentSong: feed.setCurrentSong,
    setSongs: feed.setSongs,
    loadMoreSongs: feed.loadMoreSongs,
    deleteSong: feed.deleteSong,
    getNextSong: feed.getNextSong,
    getSongById: feed.getSongById,
    fetchSongsByUser: feed.fetchSongsByUser,
    fetchSongsByUserPaginated: feed.fetchSongsByUserPaginated,
    fetchSharedSongsCount: feed.fetchSharedSongsCount,
    where,
    likeSong,
    unlikeSong,
    addComment: comments.addComment,
    addReply: comments.addReply,
    deleteComment: comments.deleteComment,
    deleteReply: comments.deleteReply,
    likeComment: comments.likeComment,
    unlikeComment: comments.unlikeComment,
    likeReply: comments.likeReply,
    unlikeReply: comments.unlikeReply,
    addEmojiReaction: reactions.addEmojiReaction,
    removeEmojiReaction: reactions.removeEmojiReaction,
    incrementPlayCount: reactions.incrementPlayCount,
    searchSongs,
    triggerThumbnailUpdate,
  }), [
    feed.songs,
    feed.totalSongCount,
    feed.currentSong,
    feed.setCurrentSong,
    feed.setSongs,
    feed.loadMoreSongs,
    feed.deleteSong,
    feed.getNextSong,
    feed.getSongById,
    feed.fetchSongsByUser,
    feed.fetchSongsByUserPaginated,
    feed.fetchSharedSongsCount,
    likeSong,
    unlikeSong,
    comments.addComment,
    comments.addReply,
    comments.deleteComment,
    comments.deleteReply,
    comments.likeComment,
    comments.unlikeComment,
    comments.likeReply,
    comments.unlikeReply,
    reactions.addEmojiReaction,
    reactions.removeEmojiReaction,
    reactions.incrementPlayCount,
    searchSongs,
    triggerThumbnailUpdate,
  ]);

  return (
    <SongsContext.Provider value={value}>{children}</SongsContext.Provider>
  );
};

export default SongsProvider;
